import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CompleteProfileModalProps {
  onProfileCompleted: () => void;
  // AUTH-ONBOARD-SELF: true só para usuário criado pelo sistema com senha temporária (must_change_password);
  // no autocadastro ("Cadastrar") a senha atual é a que a pessoa criou no cadastro
  isTemporaryPassword?: boolean;
}

import { useScopedDraftKey } from "@/hooks/useScopedDraftKey";
import { removeLegacyDraftKey } from "@/lib/scopedDraftStorage";

interface FormData {
  fullName: string;
  cpf: string;
  phone: string;
  birthDate: string;
}

export const CompleteProfileModal = ({ onProfileCompleted, isTemporaryPassword = false }: CompleteProfileModalProps) => {
  // RASCUNHOS-NAVEGADOR (I.176): rascunho do perfil com empresa + usuário na chave; lido quando a identidade fica pronta
  // e gravado só depois dessa leitura
  const STORAGE_KEY = useScopedDraftKey('complete_profile_form');
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");

  // Chave antiga (fixa) descartada: dono desconhecido não migra
  useEffect(() => {
    removeLegacyDraftKey('complete_profile_form', 'session');
  }, []);

  useEffect(() => {
    if (!STORAGE_KEY) return;
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved) as FormData;
        setFullName(data.fullName || "");
        setCpf(data.cpf || "");
        setPhone(data.phone || "");
        setBirthDate(data.birthDate || "");
      }
    } catch {
      /* rascunho ilegível — começa vazio */
    }
    setLoadedKey(STORAGE_KEY);
  }, [STORAGE_KEY]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  // Salvar dados no sessionStorage sempre que mudarem
  useEffect(() => {
    if (!STORAGE_KEY || loadedKey !== STORAGE_KEY) return;
    const formData: FormData = { fullName, cpf, phone, birthDate };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [fullName, cpf, phone, birthDate, STORAGE_KEY, loadedKey]);

  // CPF validation function
  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/[^\d]/g, '');
    if (cleanCPF.length !== 11 || /^(\d)\1{10}$/.test(cleanCPF)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleanCPF.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleanCPF.charAt(10))) return false;
    
    return true;
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{}|:<>?,.~]/.test(password);
    
    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChars,
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChars
    };
  };

  const formatCPF = (value: string) => {
    const cleaned = value.replace(/[^\d]/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})$/);
    if (match) {
      return [match[1], match[2], match[3], match[4]]
        .filter(Boolean)
        .join('.')
        .replace(/\.(\d{2})$/, '-$1');
    }
    return value;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 2) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  const handleCompleteProfile = async () => {
    // Validações
    if (!currentPassword || !fullName || !cpf || !phone || !birthDate || !newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (fullName.trim().length < 3) {
      toast.error('Nome completo deve ter pelo menos 3 caracteres');
      return;
    }

    if (!validateCPF(cpf)) {
      toast.error('CPF inválido');
      return;
    }

    const birthDateObj = new Date(birthDate);
    if (birthDateObj > new Date()) {
      toast.error('Data de nascimento não pode ser futura');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.error('A nova senha não atende aos critérios de segurança');
      return;
    }

    setLoading(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !user.email) {
        toast.error('Usuário não autenticado ou email não encontrado');
        return;
      }

      // PASSO 1: Fazer reauthentication com senha temporária
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });

      if (signInError) {
        toast.error("Senha atual incorreta: " + signInError.message);
        return;
      }

      // PASSO 2: Agora que está reautenticado, pode trocar a senha
      const { error: passwordError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (passwordError) {
        toast.error("Erro ao alterar senha: " + passwordError.message);
        return;
      }

      // PASSO 3: Atualizar perfil com dados pessoais
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          full_name: fullName.trim(),
          document_number: cpf.replace(/[^\d]/g, ''),
          document_type: 'cpf',
          phone: phone.replace(/[^\d]/g, ''),
          birth_date: birthDate,
          must_change_password: false 
        })
        .eq('user_id', user.id);

      if (profileError) {
        console.error("Error updating profile:", profileError);
        toast.error("Erro ao atualizar perfil: " + profileError.message);
        return;
      }

      toast.success("Cadastro completado com sucesso!");
      sessionStorage.removeItem(STORAGE_KEY);
      onProfileCompleted();
    } catch (error: any) {
      console.error("Error completing profile:", error);
      toast.error("Erro inesperado ao completar cadastro");
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = validatePassword(newPassword);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 overflow-y-auto py-10">
      <div className="min-h-full flex items-center justify-center p-4">
        <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-warning" />
            Complete seu Cadastro
          </CardTitle>
          <CardDescription>
            Para continuar, complete seus dados pessoais e crie uma senha definitiva.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">
              {isTemporaryPassword ? "Senha Atual (Temporária) *" : "Senha que você criou no cadastro *"}
            </Label>
            <div className="relative">
              <Input
                id="current-password"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={isTemporaryPassword ? "Digite sua senha temporária" : "Digite a senha que você criou no cadastro"}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {isTemporaryPassword
                ? "Use a senha temporária que você recebeu no primeiro login"
                : "A mesma senha que você usou para entrar agora"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="full-name">Nome Completo *</Label>
            <Input
              id="full-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Digite seu nome completo"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cpf">CPF *</Label>
            <Input
              id="cpf"
              type="text"
              value={cpf}
              onChange={(e) => setCpf(formatCPF(e.target.value))}
              placeholder="000.000.000-00"
              maxLength={14}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefone *</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(00) 00000-0000"
              maxLength={15}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="birth-date">Data de Nascimento *</Label>
            <Input
              id="birth-date"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium mb-3">Crie sua Senha Definitiva</p>
            
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha *</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite sua nova senha"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              
              {newPassword && (
                <div className="text-sm space-y-1">
                  <div className={`flex items-center gap-1 ${passwordValidation.minLength ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="text-xs">•</span> Mínimo 8 caracteres
                  </div>
                  <div className={`flex items-center gap-1 ${passwordValidation.hasUpperCase ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="text-xs">•</span> Pelo menos uma letra maiúscula
                  </div>
                  <div className={`flex items-center gap-1 ${passwordValidation.hasLowerCase ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="text-xs">•</span> Pelo menos uma letra minúscula
                  </div>
                  <div className={`flex items-center gap-1 ${passwordValidation.hasNumbers ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="text-xs">•</span> Pelo menos um número
                  </div>
                  <div className={`flex items-center gap-1 ${passwordValidation.hasSpecialChars ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="text-xs">•</span> Pelo menos um caractere especial (!@#$%^&*...)
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 mt-3">
              <Label htmlFor="confirm-password">Confirmar Nova Senha *</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme sua nova senha"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-red-600">As senhas não coincidem</p>
              )}
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Após completar seu cadastro, você terá acesso completo ao sistema.
            </AlertDescription>
          </Alert>

          <Button 
            onClick={handleCompleteProfile}
            disabled={loading || !currentPassword || !fullName || !cpf || !phone || !birthDate || !newPassword || !confirmPassword || newPassword !== confirmPassword || !passwordValidation.isValid}
            className="w-full"
          >
            {loading ? "Salvando..." : "Completar Cadastro"}
          </Button>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};
